import { MEMBER_ORDER_SKU_MAX_LENGTH, memberOrderSourceSchema } from '../orders/orders';
import { z } from 'zod';

import { apiEnvelope } from '../envelope';
import { NRT_ERROR_CODE_VALUES } from './errors';
import { nrtIsoInstantSchema } from './instant';
import { NRT_ITEM_QUANTITY_MAX } from './requests';
import { memberAddressKindSchema } from '../members/addresses';
import { memberIdSchema } from '../identity/legacy-identity-id';
import {
  NRT_OTHER_DETAIL_MAX,
  nrtMemberBlockReasonReadSchema,
  nrtOrderStatusSchema,
  nrtSideEffectSchema,
} from './vocabularies';

// ─── NRT response bodies ─────────────────────────────────────────────────────
//
// NOTHING here is `.strict()`, and that is a rule rather than an oversight
// (ADR-135 Rule 1): a response schema a client parses must tolerate unknown
// keys, or a server that adds a field breaks every installed client that has
// not been updated. Zod strips by default, which is the behaviour we want.
//
// The companion rule is ADR-002: every response is wrapped in the
// `{ data, error }` envelope via `apiEnvelope`.
//
// `apiEnvelope` alone is NOT sufficient for this contract. It makes `data` and
// `error` independently nullable, so `{ data: null, error: null }` parses —
// a response that is neither a success nor a refusal, which is precisely the
// "accepted, but nothing happened" shape this contract's invariant forbids.
// The generic envelope is shared by every surface in the repo and is not this
// slice's to change, so the constraint is added locally. Recorded as a
// residual in the spec: other surfaces remain able to emit that shape.

/**
 * The `error` object as a CLIENT parses it.
 *
 * `code` is deliberately an open `z.string()`, not an enum over
 * `NRT_ERROR_CODE_VALUES`. Closing it here would make adding a refusal code
 * later — the spec names a likely one, the dedicated entitlement config key —
 * a breaking response change under ADR-135 Rule 3: an installed client would
 * fail the whole parse rather than degrade to rendering `error.message`. A
 * client that cannot parse a refusal is worse off than one that cannot name
 * it.
 *
 * The closed check is not lost, it is relocated to where it belongs: the route
 * contracts declare their refusal sets as data, and a contract test asserts set-equality
 * against the vocabulary in both directions. That catches a typo'd code at
 * build time on the server, which is the only side that can emit one.
 */
const nrtApiErrorSchema = z.object({
  code: z.string().min(1),
  message: z.string(),
  details: z.record(z.string(), z.unknown()).optional(),
});

/**
 * The same error object as the SERVER must produce it: `code` closed over
 * `NRT_ERROR_CODES`.
 *
 * Two schemas rather than one, because the two directions have opposite
 * requirements and one schema cannot serve both. A client must stay tolerant
 * (ADR-135 Rule 3 — a code added later must not make an installed client fail
 * the whole parse instead of rendering `error.message`). A server must not be:
 * a typo'd `nrt_not_entiteld` reaching the wire is an unactionable refusal, and
 * ADR-002 Rule 6 validates responses before send, so checking here turns it
 * into a 500 the server owns rather than a dead end the member hits.
 *
 * The handler slice validates outgoing refusals against this; the client
 * parses with the tolerant one above.
 */
export const nrtProducerErrorSchema = nrtApiErrorSchema.extend({
  code: z
    .string()
    .refine(
      (value) => NRT_ERROR_CODE_VALUES.some((code) => code === value),
      'error.code must be a member of NRT_ERROR_CODES',
    ),
});

/**
 * The ADR-002 envelope, narrowed to the two states it is actually allowed to
 * be in: a success carrying non-null data with `error: null`, or a refusal
 * carrying a recognised `error.code` with `data: null`.
 *
 * Both halves matter. Without the first, a handler can return
 * `{ data: null, error: null }` and pass response validation while the caller
 * sees a success that did nothing. Without the second, a refusal
 * can arrive with no code at all, leaving a client told to branch on `code`
 * with nothing to branch on. Code MEMBERSHIP is checked at the route-contract
 * layer instead of here — see `nrtApiErrorSchema` for why closing it on a
 * client-parsed schema would be an ADR-135 Rule 3 trap.
 */
export function nrtSuccessEnvelope<T extends z.ZodType>(dataSchema: T) {
  return z
    .object({ data: dataSchema.nullable(), error: nrtApiErrorSchema.nullable() })
    .superRefine((value, ctx) => {
      // Widened by assignment, not asserted: `dataSchema` is generic, so the
      // inferred callback parameter does not carry a `data` key for TS to see.
      const envelope: { data?: unknown; error?: unknown } = value;
      if (envelope.data === null && envelope.error === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'a response must carry either data or an error, never neither',
        });
      }
      if (envelope.data !== null && envelope.error !== null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'a response must not carry both data and an error',
        });
      }
    });
}

/**
 * Display-only snapshot of where the parcel is going.
 *
 * `addressId` is echoed for support correlation and for the optional
 * `expectedAddressId` race guard on the write — it is NOT an input the member
 * chooses from. `kind` is included deliberately: a member whose parcel is
 * going to their home address because no shipping address exists should be
 * able to see that the fallback happened, rather than have it happen
 * invisibly.
 *
 * PHI. Never in log lines, metric tags, event payloads or analytics events.
 *
 * (Phrased to avoid a trailing `analytics.`: the PHI emit scanner in
 * member-addresses-phi.contract.test.ts strips `//` comments but not block
 * comments, so a sentence ending in one of its emit tokens reads as a member
 * access and the schema fields below it as a leak.)
 */
export const nrtShippingAddressSchema = z.object({
  addressId: z.string().uuid(),
  line1: z.string(),
  line2: z.string().nullable(),
  city: z.string(),
  state: z.string(),
  postalCode: z.string(),
  country: z.string(),
  kind: memberAddressKindSchema,
});
export type NrtShippingAddress = z.infer<typeof nrtShippingAddressSchema>;

/**
 * An order item as the MEMBER sees it: a server-authored display name and a
 * quantity. There is deliberately no `sku` — a SKU never crosses a member
 * token in either direction, which is one of the three independent blocks
 * stopping a member-facing call from authoring the item list.
 */
export const nrtMemberOrderItemSchema = z.object({
  displayName: z.string().min(1),
  // Same bounds as the write contract. An item is 1..NRT_ITEM_QUANTITY_MAX by
  // definition — absence is how "not ordered" is expressed — so a mapper or a
  // stray stored row producing 0 or -1 must fail response validation rather
  // than render an impossible order to the member.
  quantity: z.number().int().min(1).max(NRT_ITEM_QUANTITY_MAX),
});
export type NrtMemberOrderItem = z.infer<typeof nrtMemberOrderItemSchema>;

// ─── the supply read: five states ────────────────────────────────────────────
//
// Five, not four. A `rejected` or `cancelled` request rendered as `none` would
// leave the member unable to learn their request was refused, which is exactly
// the shape the contract's invariant forbids — so `declined` is its own state
// carrying its own reason.

/**
 * No open request. Either the member can make one (`canRequest: true`,
 * `blockedReason: null`) or they cannot, in which case `blockedReason` names
 * why in the same vocabulary the write would refuse with.
 *
 * That pairing is what stops the app rendering an enabled action whose write
 * is guaranteed to fail — and if state changes between this read and the
 * write, the write still refuses under the same name.
 */
const supplyNoneSchema = z.object({
  state: z.literal('none'),
  canRequest: z.boolean(),
  // The READ schema, not the emitting enum: a server that learns a sixth block
  // reason must cost an installed bundle one unnamed label, not its whole NRT
  // surface (ADR-135 Rule 3). `canRequest` below is what the entry point is
  // gated on, so the degraded value is still fail-closed.
  blockedReason: nrtMemberBlockReasonReadSchema.nullable(),
  /** Server-authored, localised explanation of `blockedReason`. Null when unblocked. */
  blockedMessage: z.string().trim().min(1).nullable(),
  shippingAddress: nrtShippingAddressSchema.nullable(),
});

/** Submitted, awaiting staff review. Echoes what the member asked for. */
const supplyPendingSchema = z.object({
  state: z.literal('pending'),
  requestId: z.string().uuid(),
  requestedAt: nrtIsoInstantSchema,
  wantsGum: z.boolean(),
  wantsPatch: z.boolean(),
  shippingAddress: nrtShippingAddressSchema.nullable(),
});

/** Approved, submitted to fulfilment, or dispatched. */
const supplyOnTheWaySchema = z.object({
  state: z.literal('on_the_way'),
  orderId: z.string().uuid(),
  // `.min(1)`, because this state is post-approval by definition and an order
  // with no items cannot be approved — `approve` refuses it with
  // `order_has_no_items`. An empty array here is an unreachable state that the
  // published contract would otherwise accept and hand to the client as a
  // successful supply state meaning "something is on the way, nothing will
  // ship". The guard one level down (`quantity`, `.int().min(1)`) already
  // existed for exactly this reason; the array itself did not have it.
  items: z.array(nrtMemberOrderItemSchema).min(1),
  approvedAt: nrtIsoInstantSchema.nullable(),
  /**
   * Null until the order is actually dispatched. The client must not render
   * "shipped" off the `on_the_way` state alone — `submitted` (handed to the
   * provider) and `shipped` (left the warehouse) are distinct statuses, and
   * conflating them is how a member gets told a parcel is moving when it is
   * not.
   */
  shippedAt: nrtIsoInstantSchema.nullable(),
  trackingReference: z.string().nullable(),
  /**
   * Where a NEW request would ship — NOT where this order is going.
   *
   * The same meaning this field carries on the `none` state, and populated for
   * the same reason: it is what the confirm sheet shows a member before they
   * commit to a request. It was null here while `on_the_way` offered no way to
   * request anything, and leaving it null once `canRequestAnother` exists would
   * put the member in front of a "Delivery address" heading with nothing under
   * it — confirming an order without being shown where it goes.
   *
   * It is deliberately NOT the in-flight order's address. That fact lives on
   * `GET /nrt-requests/current`, which reads it off the order; this read has no
   * order-address join and could not keep a copy correct. Two different
   * questions, answered in the two places that can answer them.
   *
   * Null only when the member has no resolvable address at all, which is also
   * the case where `canRequestAnother` is false.
   */
  shippingAddress: nrtShippingAddressSchema.nullable(),
  /**
   * Whether the member may place a NEW request while this one is in flight.
   *
   * TRUE IS THE ORDINARY CASE HERE, AND THAT IS THE POINT. An order reaches
   * `on_the_way` only once a care team member has approved it, and approval is
   * the decision the create gate waits on — so a member looking at a parcel on
   * its way may normally ask for the next one. An NRT course needs several
   * shipments; care-team review, not the order's fulfilment status, is the
   * control on how much and how often.
   *
   * It is still a server-resolved boolean rather than something a client infers
   * from `state === 'on_the_way'`, because the member's other pre-conditions can
   * lapse after they ordered — their employer stops buying NRT, their enrolment
   * ends, their address is removed. The server answers the same question the
   * write path will, so the control cannot be enabled for a create guaranteed
   * to refuse.
   *
   * Deliberately NOT named `canReorder`: that field already exists on the
   * `delivered` state and means "this one finished, start another". This one
   * means "this one is still coming, and you may have another anyway".
   */
  canRequestAnother: z.boolean(),
});

/** Delivered. */
const supplyDeliveredSchema = z.object({
  state: z.literal('delivered'),
  orderId: z.string().uuid(),
  /** `.min(1)` for the same reason as `on_the_way` — nothing was delivered if nothing shipped. */
  items: z.array(nrtMemberOrderItemSchema).min(1),
  deliveredAt: nrtIsoInstantSchema,
  canReorder: z.boolean(),
});

/**
 * Rejected or cancelled. `reasonMessage` is server-authored prose derived from
 * the staff reason — never the raw string, which may carry clinical detail
 * that is not member-appropriate. It is non-nullable: a decline the member
 * cannot be given a reason for is the refusal-without-recourse this contract
 * exists to prevent.
 */
const supplyDeclinedSchema = z.object({
  state: z.literal('declined'),
  orderId: z.string().uuid(),
  declinedAt: nrtIsoInstantSchema,
  reasonMessage: z.string().trim().min(1),
  canReRequest: z.boolean(),
});

export const nrtSupplyDataSchema = z
  .discriminatedUnion('state', [
    supplyNoneSchema,
    supplyPendingSchema,
    supplyOnTheWaySchema,
    supplyDeliveredSchema,
    supplyDeclinedSchema,
  ])
  // `canRequest` and `blockedReason` are two views of one fact and must agree.
  // Modelled independently they admit `{ canRequest: true, blockedReason:
  // 'no_shipping_address' }` — a client following `canRequest` enables an action
  // the write is guaranteed to refuse — and `{ canRequest: false,
  // blockedReason: null }`, a member blocked with nothing to show them. Both
  // are the read/write parity invariant failing, so they are rejected here
  // rather than left to each caller to remember.
  //
  // Refined on the union, not the variant: `discriminatedUnion` takes plain
  // objects, and a refined variant is no longer one.
  .superRefine((value, ctx) => {
    // `on_the_way` carries the SAME invariant as `none`, for the same reason.
    //
    // Both states can offer a request, and the order sheet renders a "Delivery
    // address" heading above whatever `shippingAddress` holds — so a payload
    // that says "you may order" while carrying no address puts a member in front
    // of an enabled Confirm and a blank address block. `none` has been refined
    // against that from the start; `on_the_way` only gained the affordance in
    // the repeat-order change, and the refinement did not follow it. The
    // contradiction was therefore representable, and a fixture in this module's
    // own test asserted that it parsed.
    //
    // Enforced at the serialization boundary rather than trusted to the
    // producer: a handler bug throws here instead of reaching a client as a
    // payload whose two halves disagree.
    if (value.state === 'on_the_way' && value.canRequestAnother && !value.shippingAddress) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['shippingAddress'],
        message: 'canRequestAnother is true, so a resolved shipping address is required',
      });
    }
    if (value.state !== 'none') return;
    if (value.canRequest && value.blockedReason !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['blockedReason'],
        message: 'canRequest is true, so there can be no blockedReason',
      });
    }
    if (!value.canRequest && value.blockedReason === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['blockedReason'],
        message: 'canRequest is false, so a blockedReason is required',
      });
    }
    // `canRequest: true` means no pre-condition blocks the write — and one of
    // those pre-conditions is having an address on file. An enabled state with
    // none is a button whose write returns `no_shipping_address`.
    if (value.canRequest && value.shippingAddress === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['shippingAddress'],
        message: 'canRequest is true, so a resolved shipping address is required',
      });
    }
    // Both directions. A reason the member is never shown is a refusal they
    // cannot act on; a message with no reason renders refusal copy beside an
    // enabled action, which is the same contradiction facing the other way.
    if (value.blockedReason !== null && value.blockedMessage === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['blockedMessage'],
        message: 'a blockedReason requires a member-readable blockedMessage',
      });
    }
    if (value.blockedReason === null && value.blockedMessage !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['blockedMessage'],
        message: 'a blockedMessage without a blockedReason has nothing to explain',
      });
    }
  });
export type NrtSupplyData = z.infer<typeof nrtSupplyDataSchema>;

export const nrtSupplyResponseSchema = nrtSuccessEnvelope(nrtSupplyDataSchema);
export type NrtSupplyResponse = z.infer<typeof nrtSupplyResponseSchema>;

// ─── the create response ─────────────────────────────────────────────────────

/**
 * The created request.
 *
 * Every field is required. There is no `ok` boolean, no nullable id and no
 * variant that parses as "accepted but nothing happened" — a success response
 * indistinguishable from a no-op is the first thing the invariant rules out.
 * `addressId` echoes what the server actually bound, so the client can show
 * the member where it is going without ever having chosen it.
 */
export const nrtRequestCreateDataSchema = z.object({
  requestId: z.string().uuid(),
  requestedAt: nrtIsoInstantSchema,
  wantsGum: z.boolean(),
  wantsPatch: z.boolean(),
  addressId: z.string().uuid(),
});
export type NrtRequestCreateData = z.infer<typeof nrtRequestCreateDataSchema>;

export const nrtRequestCreateResponseSchema = nrtSuccessEnvelope(nrtRequestCreateDataSchema);
export type NrtRequestCreateResponse = z.infer<typeof nrtRequestCreateResponseSchema>;

// ─── the current-request read ────────────────────────────────────────────────

/**
 * The member's current request and the derived status of its order.
 *
 * `data` is nullable here and that is NOT a refusal: having never made a
 * request is a legitimate empty read, distinct from being refused one. The
 * refusal cases live on the supply read's `blockedReason` and on the create
 * route's error codes.
 */
export const nrtCurrentRequestDataSchema = z.object({
  requestId: z.string().uuid(),
  requestedAt: nrtIsoInstantSchema,
  wantsGum: z.boolean(),
  wantsPatch: z.boolean(),
  sideEffects: z.array(nrtSideEffectSchema),
  orderId: z.string().uuid().nullable(),
  orderStatus: nrtOrderStatusSchema.nullable(),
  shippingAddress: nrtShippingAddressSchema.nullable(),
});
export type NrtCurrentRequestData = z.infer<typeof nrtCurrentRequestDataSchema>;

/**
 * The one response where `{ data: null, error: null }` is a real answer — the
 * member has never made a request — rather than the no-op success
 * `nrtSuccessEnvelope` forbids.
 *
 * It still may not carry data AND an error at once: that pairing is ambiguous
 * on every response in the repo, and a client shown both has no defined
 * behaviour. So this is its own envelope rather than the plain one, which
 * permits it.
 */
export const nrtCurrentRequestResponseSchema = apiEnvelope(
  nrtCurrentRequestDataSchema.nullable(),
).superRefine((value, ctx) => {
  const envelope: { data?: unknown; error?: unknown } = value;
  if (envelope.data !== null && envelope.error !== null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'a response must not carry both data and an error',
    });
  }
});
export type NrtCurrentRequestResponse = z.infer<typeof nrtCurrentRequestResponseSchema>;

// ─── staff projections ───────────────────────────────────────────────────────

/** An order item as STAFF see it — SKUs are staff-only. */
export const nrtOrderItemSchema = z.object({
  // Same bound as the write and the CHECK. An unbounded read would accept a
  // stored value no write could have produced, which is the read-side half of
  // the same disagreement.
  sku: z.string().min(1).max(MEMBER_ORDER_SKU_MAX_LENGTH),
  displayName: z.string().min(1),
  quantity: z.number().int().min(1).max(NRT_ITEM_QUANTITY_MAX),
});
export type NrtOrderItem = z.infer<typeof nrtOrderItemSchema>;

/** One entry of the order's append-only status history. */
export const nrtOrderStatusEventSchema = z.object({
  status: nrtOrderStatusSchema,
  occurredAt: nrtIsoInstantSchema,
  actorKind: z.string(),
  actorInternalUserId: z.string().uuid().nullable(),
  reason: z.string().nullable(),
});
export type NrtOrderStatusEvent = z.infer<typeof nrtOrderStatusEventSchema>;

/**
 * The staff order projection, returned by EVERY staff write rather than just
 * the field that changed — read-your-writes, so a client cannot hold a stale
 * status after a transition moved it.
 *
 * `status` is the DERIVED current status (the latest status event), never a
 * stored column; `statusHistory` is what it was derived from, so the staff
 * surface can show its own provenance.
 */
/**
 * Statuses an order can hold while legitimately carrying no items.
 *
 * `created` is the state `approve` refuses with `order_has_no_items` and is
 * exactly what staff open the order to fix; `rejected` and `cancelled` can be
 * reached straight from it without items ever being set. Every other status is
 * post-approval, and approval requires a non-empty set — so an empty array
 * there is unreachable, and the schema says so below.
 *
 * Exported because the 1.0 order import asks the same question before it
 * writes an order: an imported order outside this set with no item is one no
 * surface can render, so the import refuses it rather than writing it.
 */
export const NRT_ORDER_STATUSES_WITHOUT_ITEMS: readonly string[] = Object.freeze([
  'created',
  'rejected',
  'cancelled',
]);

export const nrtOrderDataSchema = z
  .object({
    orderId: z.string().uuid(),
    requestId: z.string().uuid().nullable(),
    memberId: memberIdSchema,
    status: nrtOrderStatusSchema,
    items: z.array(nrtOrderItemSchema),
    statusHistory: z.array(nrtOrderStatusEventSchema),
    addressId: z.string().uuid(),
    placedAt: nrtIsoInstantSchema,
    /**
     * The address row the order is bound to (`member_orders.address_id`) —
     * where the member confirmed the parcel should go when they ordered, NOT
     * their current address re-read now (ADR-130 Rule 3). A later address
     * change appends a new row and leaves this one, and this order, alone.
     *
     * Required, not nullable: the composite FK makes an order without its
     * row unreachable, and a nullable field would let a staff client render
     * an order with nowhere to send it.
     *
     * PHI. Served only on per-order staff reads, never on the queue list, and
     * never in log lines, metric tags or event payloads.
     */
    shippingAddress: nrtShippingAddressSchema,
  })
  // Keyed on status rather than a flat `.min(1)`, because the exemption is
  // real for three statuses and unreachable for the other four. An earlier
  // revision left this schema open for ALL seven with a comment describing
  // only the `created` case — so `{ status: 'delivered', items: [] }` parsed,
  // which is the same unreachable-but-accepted state the member-facing pair
  // was fixed for, one level in. Caught in review.
  .superRefine((value, ctx) => {
    if (NRT_ORDER_STATUSES_WITHOUT_ITEMS.includes(value.status)) return;
    if (value.items.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['items'],
        message: `an order in status '${value.status}' is past approval and must carry at least one item`,
      });
    }
  });
export type NrtOrderData = z.infer<typeof nrtOrderDataSchema>;

export const nrtOrderResponseSchema = nrtSuccessEnvelope(nrtOrderDataSchema);
export type NrtOrderResponse = z.infer<typeof nrtOrderResponseSchema>;

/** One row of the staff request queue. */
export const nrtRequestListItemSchema = z.object({
  requestId: z.string().uuid(),
  orderId: z.string().uuid().nullable(),
  memberId: memberIdSchema,
  requestedAt: nrtIsoInstantSchema,
  wantsGum: z.boolean(),
  wantsPatch: z.boolean(),
  sideEffects: z.array(nrtSideEffectSchema),
  /**
   * Whether the member wrote free-text detail — NOT the text itself.
   *
   * Data minimisation (ADR-069): `other_detail` is member-authored clinical
   * free text, and a queue renders many rows at once, so carrying it here
   * would spray PHI across every list impression for staff who are only
   * triaging. The boolean is enough to triage on; the text belongs on a
   * per-request read, opened deliberately for the one request being acted on.
   *
   * The read it points at is `GET /api/v1/internal/nrt-requests/{requestId}`
   * (`nrtRequestDetailDataSchema` below), published in this slice precisely so
   * the queue is not a surface that announces detail a client has no
   * documented way to open.
   */
  hasOtherDetail: z.boolean(),
  status: nrtOrderStatusSchema.nullable(),
  /**
   * Where the request came from. `legacy_import` marks a request carried over
   * from the 1.0 app — including one the import made up for a 1.0 order that
   * had none — so staff can tell that the 1.0 team may already be handling it
   * before they act on it here.
   *
   * Added to a published staff response in place. The only client is the
   * Atlas SPA, which ships with this package and is served whole, so no
   * installed bundle older than the field can call the route; a stale tab
   * strips the key, as every non-strict response schema does.
   */
  source: memberOrderSourceSchema,
});
export type NrtRequestListItem = z.infer<typeof nrtRequestListItemSchema>;

/**
 * One request as staff read it when they open it to act — the queue row plus
 * the member-authored free text the row deliberately withholds.
 *
 * Derived from `nrtRequestListItemSchema` rather than redeclared: the detail IS
 * the row plus the text, and deriving it means a field added to the queue
 * cannot go missing here. `hasOtherDetail` is dropped because it would be
 * redundant beside the value it describes.
 *
 * `otherDetail` is member-authored clinical PHI. It is carried on this
 * per-request read and on no list surface — that split is the whole reason the
 * row publishes a boolean (ADR-069 data minimisation), and it only works if
 * this route exists to open. Never log it, never put it in a metric tag, event
 * payload or analytics property.
 */
export const nrtRequestDetailDataSchema = nrtRequestListItemSchema
  .omit({ hasOtherDetail: true })
  .extend({
    otherDetail: z.string().trim().min(1).nullable(),
    /**
     * The request's order, in the same projection every staff order action
     * returns — items, derived status, status history and the address it is
     * bound to — so a member page can show and act on it without a write.
     *
     * `null` when the request has no order (the legacy import's shape), and
     * also for a legacy order with no status events: that order cannot be
     * acted on, and `orderId` still tells it apart from "no order at all".
     *
     * Carries `shippingAddress`, which is PHI: this per-request read is one of
     * the two places it is served, and the queue row never carries it.
     */
    order: nrtOrderDataSchema.nullable(),
  })
  // The read carries the same correlation the write enforces. Without it this
  // schema validated `{ sideEffects: ['other'], otherDetail: null }` — a
  // request the create route cannot produce, and precisely the blind-approval
  // state publishing this route was meant to end: staff open the request
  // because the row said detail exists, and the payload says there is none.
  // Bounds too, so a response cannot carry more than the write accepted.
  .superRefine((value, ctx) => {
    const selectedOther = value.sideEffects.includes('other');
    if (selectedOther && value.otherDetail === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['otherDetail'],
        message: "sideEffects includes 'other', so otherDetail cannot be null",
      });
    }
    if (!selectedOther && value.otherDetail !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['otherDetail'],
        message: "otherDetail is only meaningful when sideEffects includes 'other'",
      });
    }
    if (value.otherDetail !== null && value.otherDetail.length > NRT_OTHER_DETAIL_MAX) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['otherDetail'],
        message: `otherDetail exceeds ${NRT_OTHER_DETAIL_MAX} characters`,
      });
    }
  });
export type NrtRequestDetailData = z.infer<typeof nrtRequestDetailDataSchema>;

export const nrtRequestDetailResponseSchema = nrtSuccessEnvelope(nrtRequestDetailDataSchema);
export type NrtRequestDetailResponse = z.infer<typeof nrtRequestDetailResponseSchema>;

/**
 * Keyset-paginated queue. `nextCursor` is null on the last page — there is no
 * total and no offset, matching the member-list precedent.
 */
export const nrtRequestListDataSchema = z.object({
  requests: z.array(nrtRequestListItemSchema),
  nextCursor: z.string().nullable(),
});
export type NrtRequestListData = z.infer<typeof nrtRequestListDataSchema>;

export const nrtRequestListResponseSchema = nrtSuccessEnvelope(nrtRequestListDataSchema);
export type NrtRequestListResponse = z.infer<typeof nrtRequestListResponseSchema>;

/**
 * Every response envelope, so a test can assert the non-strict posture across
 * all of them by iteration rather than by naming each one.
 */
export const NRT_RESPONSE_SCHEMAS = {
  nrtSupplyResponseSchema,
  nrtRequestDetailResponseSchema,
  nrtRequestCreateResponseSchema,
  nrtCurrentRequestResponseSchema,
  nrtOrderResponseSchema,
  nrtRequestListResponseSchema,
} as const;

/**
 * The INNER data schemas, separately from their envelopes.
 *
 * Both maps exist because testing only the envelopes is vacuous for the
 * property that matters: `apiEnvelope` wraps everything in a non-strict
 * `{ data, error }` object, so an unknown key added at the top level is
 * stripped by the wrapper no matter how strict the payload is. A `.strict()`
 * slipped onto a data schema — the actual ADR-135 Rule 1 violation, and the
 * one that breaks installed clients when the server adds a field — is
 * invisible unless the unknown key is injected INSIDE `data`. That gap was
 * found by mutation, not by reading.
 */
export const NRT_RESPONSE_DATA_SCHEMAS = {
  nrtSupplyDataSchema,
  nrtRequestDetailDataSchema,
  nrtRequestCreateDataSchema,
  nrtCurrentRequestDataSchema,
  nrtOrderDataSchema,
  nrtRequestListDataSchema,
} as const;

/**
 * The member-facing data schemas only — the set the hygiene tests walk when
 * asserting that no SKU and no substance name reaches a member token.
 */
export const NRT_MEMBER_FACING_DATA_SCHEMAS = {
  nrtSupplyDataSchema,
  nrtRequestCreateDataSchema,
  nrtCurrentRequestDataSchema,
} as const;
